import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, Search as SearchIcon } from 'lucide-react';
import { FieldShell } from './FieldShell';
import { useResolvePrePopulatedOption, useSearchOptions } from '../hooks/useSearchOptions';
import type { SearchOptionRow } from '../services/optionsService';
import type { FieldComponentProps } from './types';

const isStoredValue = (v: unknown): v is string | number =>
  (typeof v === 'string' && v.length > 0) || typeof v === 'number';

export const SearchField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const meta = field.search_meta;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<SearchOptionRow | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const optionsQuery = useSearchOptions({
    searchMeta: meta,
    searchValue: query,
    enabled: open && !!meta,
  });

  const prePopulated = useResolvePrePopulatedOption({
    searchMeta: meta,
    prePopulatedValue: !selectedRow && isStoredValue(value) ? value : undefined,
  });

  // When backend resolves a pre-populated value into a row, hydrate the display.
  useEffect(() => {
    if (!selectedRow && prePopulated.data) {
      setSelectedRow(prePopulated.data);
    }
  }, [prePopulated.data, selectedRow]);

  // Auto-select when `auto_select` is true and exactly one result.
  useEffect(() => {
    if (!field.auto_select) return;
    if (selectedRow) return;
    const rows = optionsQuery.data;
    if (open && rows && rows.length === 1) {
      const row = rows[0];
      setSelectedRow(row);
      onChange(row.value);
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsQuery.data, field.auto_select, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const displayValue = useMemo(() => {
    if (selectedRow) return selectedRow.primary;
    if (prePopulated.data) return prePopulated.data.primary;
    return '';
  }, [selectedRow, prePopulated.data]);

  if (!meta) {
    return (
      <FieldShell field={field} error={error || 'Search field is missing search_meta in the schema.'}>
        <div className="form-input bg-rgf-gray-50 text-rgf-gray-500">unconfigured</div>
      </FieldShell>
    );
  }

  const handleSelect = (row: SearchOptionRow) => {
    setSelectedRow(row);
    onChange(row.value);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedRow(null);
    setQuery('');
    onChange('');
  };

  return (
    <FieldShell field={field} error={error}>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          id={field.title}
          className="form-input flex items-center justify-between w-full text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={displayValue ? 'text-rgf-gray-900' : 'text-rgf-gray-400'}>
            {displayValue || `Select ${field.display_title || field.title}`}
          </span>
          <span className="flex items-center gap-1">
            {displayValue && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear selection"
                className="text-rgf-gray-400 hover:text-rgf-gray-700"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClear();
                  }
                }}
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-rgf-gray-400" />
          </span>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-rgf-gray-200 bg-white shadow-lg">
            <div className="px-2 pt-2">
              <div className="relative">
                <SearchIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-rgf-gray-400 pointer-events-none" />
                <input
                  autoFocus
                  type="text"
                  className="form-input pl-8"
                  placeholder="Type to search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-2">
              {optionsQuery.isLoading || optionsQuery.isFetching ? (
                <div className="px-3 py-2 text-sm text-rgf-gray-500">Loading…</div>
              ) : optionsQuery.error ? (
                <div className="px-3 py-2 text-sm text-red-600">
                  {(optionsQuery.error as Error).message || 'Failed to load options.'}
                </div>
              ) : !optionsQuery.data || optionsQuery.data.length === 0 ? (
                <div className="px-3 py-2 text-sm text-rgf-gray-500">No matches.</div>
              ) : (
                optionsQuery.data.map((row) => (
                  <button
                    type="button"
                    key={String(row.value)}
                    className="w-full text-left px-3 py-2 hover:bg-rgf-gray-50"
                    onClick={() => handleSelect(row)}
                  >
                    <div className="text-sm text-rgf-gray-900">{row.primary || '—'}</div>
                    {row.secondary && (
                      <div className="text-xs text-rgf-gray-500">{row.secondary}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </FieldShell>
  );
};
